import React, { useState } from 'react';
import { authorizedFetch } from '../utils/api';
import { BACKEND_URL } from '../utils/backend';

// Spinner copied from KnowledgeBase
function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

export default function MixMatch() {
  const [personFile, setPersonFile] = useState(null);
  const [personPreview, setPersonPreview] = useState('');
  const [lookUrl, setLookUrl] = useState('');
  const [looks, setLooks] = useState([]);
  const [resultUrl, setResultUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState('cloud'); // default to cloud
  const [renderTime, setRenderTime] = useState(null);
  const [showRenderTime, setShowRenderTime] = useState(false);
  const renderStartRef = React.useRef(null);

  // Fetch available looks (cloud/local/WWD)
  React.useEffect(() => {
    renderStartRef.current = performance.now();
    async function fetchLooks() {
      let url = '';
      if (source === 'cloud') {
        url = '/fashion-looks/latest?source=cloud&limit=30';
      } else if (source === 'wwd') {
        url = '/api/wwd-looks?limit=30';
      } else {
        url = '/fashion-looks/latest?source=local&limit=30';
      }
      try {
        const res = await authorizedFetch(url);
        const data = await res.json();
        setLooks(Array.isArray(data) ? data : []);
        // Calculate render time
        const end = performance.now();
        const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
        setRenderTime(seconds);
        setShowRenderTime(true);
        setTimeout(() => setShowRenderTime(false), 5000);
      } catch (err) {
        setLooks([]);
      }
    }
    fetchLooks();
  }, [source]);

  const handlePersonChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file.');
      return;
    }
    setPersonFile(file);
    setPersonPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleLookSelect = (url) => {
    setLookUrl(url);
  };

  const handleGenerate = async () => {
    if (!personFile || !lookUrl) {
      setError('Please upload a personal photo and select a fashion look.');
      return;
    }
    setLoading(true);
    setError('');
    setResultUrl('');
    const formData = new FormData();
    formData.append('person_image', personFile);
    formData.append('fashion_look_url', lookUrl);
    try {
      const res = await fetch(`${BACKEND_URL}/mix-match`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Failed to generate image.');
      const data = await res.json();
      setResultUrl(data.cloudinary_url || data.generated_image_url);
    } catch (err) {
      setError('Generation failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 max-w-6xl mx-auto animate-fade-in" style={{ position: 'relative' }}>
      <h1 className="text-2xl font-bold mb-6">🎨 Mix & Match: AI Styling Preview</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Column 1: Upload personal photo */}
        <div className="flex flex-col items-center">
          <div className="mb-4 w-full">
            <label className="block font-semibold mb-2">1. Upload Your Photo</label>
            <input type="file" accept="image/*" onChange={handlePersonChange} />
            {personPreview && (
              <img src={personPreview} alt="Personal Preview" className="mt-4 rounded shadow max-h-64" />
            )}
          </div>
        </div>
        {/* Column 2: Select fashion look */}
        <div className="flex flex-col items-center">
          <div className="mb-4 w-full flex gap-2 items-center">
            <label className="block font-semibold mb-2 flex-1">2. Select Fashion Look</label>
            <select value={source} onChange={e => setSource(e.target.value)} className="border rounded px-2 py-1 text-sm">
              <option value="cloud">Cloudinary</option>
              <option value="local">Local</option>
              <option value="wwd">WWD</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {looks.map((look, idx) => (
              <div key={look.image_url || idx} className="flex flex-col items-center">
                <img
                  src={look.image_url}
                  alt="Fashion Look"
                  className={`rounded cursor-pointer border-2 ${lookUrl === look.image_url ? 'border-blue-500' : 'border-transparent'}`}
                  style={{ maxHeight: 100, objectFit: 'cover' }}
                  onClick={() => handleLookSelect(look.image_url)}
                />
                {typeof look.machine_score !== 'undefined' && (
                  <div className="text-xs text-blue-600 mt-1">Machine: <span className="font-bold">{look.machine_score ?? '-'}</span></div>
                )}
                {typeof look.buyer_score !== 'undefined' && (
                  <div className="text-xs text-green-600 mt-1">Buyer: <span className="font-bold">{look.buyer_score ?? '-'}</span></div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Column 3: Display result */}
        <div className="flex flex-col items-center">
          <div className="mb-4 w-full">
            <label className="block font-semibold mb-2">3. AI-Generated Result</label>
            {loading ? (
              <div className="text-center text-gray-500">Generating...</div>
            ) : resultUrl ? (
              <img src={resultUrl} alt="Result" className="rounded shadow max-h-64" />
            ) : (
              <div className="text-gray-400">Result will appear here</div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-center">
        <button
          className="bg-blue-600 text-white px-8 py-2 rounded font-semibold hover:bg-blue-700 transition disabled:opacity-50"
          onClick={handleGenerate}
          disabled={loading || !personFile || !lookUrl}
        >
          {loading ? 'Generating...' : 'Generate'}
        </button>
      </div>
      {error && <div className="mt-4 p-2 bg-red-100 border border-red-300 text-red-800 rounded">{error}</div>}
      {showRenderTime && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }} className="bg-gray-800 text-white text-xs px-3 py-1 rounded shadow animate-fade-in">
          Render Time: {renderTime} seconds
        </div>
      )}
    </div>
  );
} 